/**
 * figureApi — 그림(SVG) 편집·재요청·이력.
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md (dev 배포·e2e 24/24 · 커밋 3def468)
 * 회신: docs/exam-builder-v2/BE-20260829-reply-fe-0828.md §2 — 질의 8건 전부 답이 왔다(계약 §8 에 반영)
 *
 * 대상이 둘인데 **경로만 다르고 계약은 거의 같다**. 그래서 base 문자열 하나로 가른다 —
 * 이 모듈이 「OCR 이냐 은행이냐」를 아는 유일한 자리는 figureBase 한 함수다.
 *
 * 봉투 { status, message, status_code, data } — 성공 판정 status === 'success'.
 * 409 는 data.reason 으로 갈린다(stale·invalid_spec·no_original·in_progress).
 *
 * 왜 공용 fetchWithTokenRefresh 를 안 쓰나: 그쪽은 400·403 을 고정 문구로 갈아 끼우고
 * **상태코드와 서버 message 를 둘 다 버린다**(utils/apiUtils.js). 이 API 는 같은 409 가
 * 네 갈래고 화면이 서로 다른 말을 해야 해서, 그 두 정보를 잃으면 분기할 수 없다.
 * problemApi·analysisApi·variationApi 가 같은 이유로 각자 사본을 두고 있다(problemApi.js:34 주석).
 */
import { fetchAPI, AUTH_REDIRECT } from '../../utils/apiUtils';
import { FIGURE_SVG_EDITOR } from '../../utils/roles';
// 서버가 `trace_texts` 를 안 실어 줄 때 그림 자신에게서 읽는다 — 그 근거는 figureTrace 머리주석.
import { readTraceTextsFromSvg } from './figureTrace';
// 「본문에서 사라진 그림」을 고르는 판정. 순수 함수라 여기서 부르고, 시험은 figureMatch.test 가 덮는다.
import { planFigureDeletions } from './figureMatch';

export { AUTH_REDIRECT };

/**
 * 그림 API 의 뿌리 경로.
 * - OCR 검수(승격 전): /api/v2/ocr/problems/{ocr_prob_id}
 * - 문제은행(문제 /problem-hub): /api/v2/problems/{problem_id}
 */
export function figureBase(target, id) {
  return target === 'ocr' ? `/api/v2/ocr/problems/${id}` : `/api/v2/problems/${id}`;
}

/**
 * 상태코드가 필요한 호출 공용. 실패 시 Error.httpStatus + Error.data 를 실어 던진다.
 * body 를 주지 않으면 GET 이다 — 이 API 의 읽기(목록·이력)도 401·404 를 갈라야 해서 같은 통로를 쓴다.
 */
async function call(path, { method = 'GET', payload = undefined } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  const options = { method, headers: { Authorization: token } };
  if (payload !== undefined) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(payload);
  }
  let response;
  try {
    response = await fetchAPI(path, options);
  } catch (_) {
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다 — 공용 유틸과 같은 뒷정리.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 아래에서 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    const err = new Error(body?.message || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.data = body?.data ?? null; // 409 의 reason·issues 를 화면까지 나른다
    throw err;
  }
  return body.data;
}

/**
 * 그림을 어떤 눈으로 옮길지 — 계약 §9-4 「변환 모드」(2026-08-31).
 *
 * 값은 서버가 정한 것이라 이름을 바꾸지 않는다. **화면에 낼 말은 figureText 가 갖는다** —
 * 여기 한글을 박으면 API 모듈이 문구의 출처가 되어 다음 사람이 두 자리를 뒤진다.
 *
 * · faithful  기본. 도판(그래프·도형)을 좌표·식으로 충실히 옮긴다.
 *             삽화·사진이라 거절되면 응답에 `suggest:"trace"` 가 실린다.
 * · trace     사진의 윤곽을 그대로 따라 그리고(potrace), 그림 속 인쇄 문구를 진짜 <text> 로 얹는다.
 *             **문제은행 재요청에만 있다**(계약 §9-4 「은행 재요청만, dev 배포」).
 * · schematic 문제 본문을 읽고 풀이용 도식으로 다시 그린다(삽화 → 원+반지름+높이).
 *
 * 값을 안 보내면 서버 기본값(faithful)이다. **기본일 때는 키를 싣지 않는다** — 계약이 배포 중인
 * dev 를 가리키고 있어, 아직 `mode` 를 모르는 배포본에 400 을 맞을 자리를 만들지 않는다.
 */
export const FIGURE_MODES = ['faithful', 'trace', 'schematic'];
export const FIGURE_MODE_DEFAULT = 'faithful';

/** 보낼 값인가 — 기본값과 모르는 값은 키를 싣지 않는다. */
export function figureModePayload(mode) {
  if (FIGURE_SVG_EDITOR) return undefined;
  return FIGURE_MODES.includes(mode) && mode !== FIGURE_MODE_DEFAULT ? mode : undefined;
}

/** 표준 SVG 저장 — 서버 정제본과 판 번호를 함께 갱신한다. 기존 figspec 쓰기는 전환일까지 유지. */
export async function saveFigureSvg(target, id, field, index, { svgText, expectedRevision, expectedUpdatedAt } = {}, getAccessToken) {
  if (!Number.isInteger(expectedRevision) || expectedRevision < 1) throw new Error('그림 판 번호가 없습니다. 그림을 다시 읽어 주세요.');
  return call(`${figureBase(target, id)}/figures/${field}/${index}`, {
    method: 'PATCH',
    payload: { svg_text: svgText, expected_revision: expectedRevision, ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}) },
  }, getAccessToken, '그림을 저장하지 못했습니다.');
}

/** AI 호출 없이 보관 원본 사진으로 복귀한다. */
export async function restoreOriginal(target, id, field, index, { expectedUpdatedAt } = {}, getAccessToken) {
  return call(`${figureBase(target, id)}/figures/${field}/${index}`, {
    method: 'PATCH', payload: { use_original: true, ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}) },
  }, getAccessToken, '원본 사진으로 되돌리지 못했습니다.');
}

/**
 * 명세 → SVG 즉시 미리보기 — POST /api/v2/figspec/render (계약 §2)
 *
 * **LLM 도 DB 도 쓰지 않는다. 수 ms.** 사용자가 숫자를 고칠 때마다 불러도 된다(디바운스 200ms 권장).
 * 응답 { ok, svg(data-URI), svg_text, width, height, alt, issues[] }.
 *
 * ⚠ **명세가 무효여도 200 이다**(400 이 아니다) — ok:false + issues[]. 이것을 오류로 다루면
 * 「고칠 수 없다」가 되어 편집기의 존재 이유가 사라진다. 400 은 `spec` 키 자체가 없을 때뿐이다.
 */
export async function renderFigspec(spec, getAccessToken) {
  return call(
    '/api/v2/figspec/render',
    { method: 'POST', payload: { spec } },
    getAccessToken,
    '미리보기를 그리지 못했습니다.',
  );
}

/**
 * 이 문제의 그림 목록 — GET {base}/figures (계약 §3-1·§4-1)
 * data = { items[], updated_at } (+ 대상 식별자). items 는 body 그림들 → solution 그림들 순서.
 *
 * ⚠ **첫 조회가 쓰기(seed)를 동반한다**(계약 §6) — 각 그림의 현재 상태가 이력 version 1(관측)로
 * 기록되고 원본이 보관된다. 그래서 화면을 열자마자 부르지 않는다. 사용자가 그림을 실제로
 * 다루려 할 때만 부른다. (그 seed 는 낙관락 토큰을 **전진시키지 않는다** — 회신 §2 Q4 가 실측으로
 * 확인했다. 이 응답의 updated_at 을 그대로 다음 쓰기의 expected_updated_at 으로 쓰면 된다.)
 */
export async function fetchFigures(target, id, getAccessToken) {
  return call(`${figureBase(target, id)}/figures`, {}, getAccessToken, '그림 목록을 불러오지 못했습니다.');
}

/**
 * 서버가 그림 이미지로 받아 주는 것 — 계약 §10(2026-09-04 회신 §1·§7).
 *
 * **화면이 먼저 막는 이유**: 서버는 형식이 아니면 400, 디코드 2MB 초과면 413 을 낸다. 클립보드
 * 캡처는 수백 KB 가 예사고 화면 캡처는 2MB 를 쉽게 넘는데, 그때 수 MB 를 올려 보내고 나서
 * 거절당하면 사람은 기다린 만큼 잃는다. 보내기 전에 같은 규칙으로 재는 편이 짧다.
 *
 * ⚠ 상한은 **디코드 후** 크기다(base64 문자열 길이가 아니다 — 그쪽이 약 4/3 배 크다).
 * 회신 §1: 「`image` 는 디코드 후 2MB 이하여야 합니다(현재 NKB)」.
 */
export const FIGURE_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
/** SVG data-URI 는 400 이다(회신 §1) — 도판을 그대로 올리는 길이 아니다. */
const FIGURE_IMAGE_DATA_URI = /^data:image\/(png|jpeg|jpg|webp);base64,([A-Za-z0-9+/]+={0,2})$/;

/**
 * 이 data-URI 를 서버가 받아 주는가 — 등록·교체·재요청이 모두 같은 검사를 지난다.
 *
 * 순수 함수다(figureApi.test.js). 크기는 **재지 않고 센다** — base64 는 4글자가 3바이트라
 * 문자열 길이만으로 디코드 크기가 나온다. 실제로 디코드하면 수 MB 를 한 번 더 메모리에 올린다.
 *
 * @returns { ok:true, mime, bytes } | { ok:false, reason:'format' } | { ok:false, reason:'size', bytes }
 */
export function parseImageDataUri(dataUri) {
  const m = typeof dataUri === 'string' ? dataUri.match(FIGURE_IMAGE_DATA_URI) : null;
  if (!m) return { ok: false, reason: 'format' };
  const b64 = m[2];
  // 4의 배수가 아니면 base64 가 아니다 — 서버가 「디코드 실패」로 400 을 낼 값이다.
  if (b64.length % 4 !== 0) return { ok: false, reason: 'format' };
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  const bytes = (b64.length / 4) * 3 - pad;
  if (bytes > FIGURE_IMAGE_MAX_BYTES) return { ok: false, reason: 'size', bytes };
  return { ok: true, mime: `image/${m[1] === 'jpg' ? 'jpeg' : m[1]}`, bytes };
}

/**
 * 이번 쓰기가 **편집본을 버렸는가** — 계약 §10(회신 §2).
 *
 * 2026-09-04 부터 OCR 그림 쓰기는 편집본을 폐기하지 않고 **같은 순번의 image 노드만** 고친다.
 * 다만 순번 대조가 안 되면(검수자가 참고 이미지를 본문에 따로 넣어 둔 편집본 등) 종전대로
 * 폐기하고 이 키를 `true` 로 실어 준다. 화면은 그것을 읽어 **사후에** 알린다 —
 * 미리 경고할 수 없다: 대조가 될지는 서버만 안다.
 *
 * 실리는 응답: PATCH · DELETE · reconvert(converted:true) · restore · generate(generated:true) · 등록.
 * 키가 아예 없으면 false 로 읽는다 — 옛 배포분과 섞여 돌 때 「폐기됐다」고 거짓말하지 않는다.
 */
export function readEditedCopyDropped(source) {
  return source?.edited_copy_dropped === true;
}

/**
 * 그림 하나 저장 — PATCH {base}/figures/{field}/{index} (계약 §3-2·§4-2)
 *
 * spec: 명세 객체 | null(명세를 버리고 원본 픽셀로).
 * source: 'edit'(사람이 고침, 기본) | 'convert'(변환 결과 채택). 이력의 사유로 남는다.
 *
 * - spec:null 인데 보관된 원본이 없으면 409 no_original, 이미 픽셀인 그림에 보내면 400 이다.
 *   그래서 호출부는 item.has_original 과 item.kind 를 보고 버튼을 잠근다 — 눌리면 반드시
 *   실패하는 버튼을 내지 않는다.
 * - 문제은행은 이 저장이 **본문 이력에 버전을 하나 쌓고** Problems.updated_at 을 전진시킨다.
 *   즉 그림만 고쳐도 [수정 이력]에서 되돌릴 수 있다.
 * - OCR 의 편집본은 **폐기되지 않는다**(계약 §10 · 2026-09-04). 서버가 같은 순번 노드의
 *   src·width·height·altText 만 갈아 끼운다. 대조가 안 될 때만 폐기하고 응답에
 *   `edited_copy_dropped:true` 를 싣는다 → readEditedCopyDropped.
 */
export async function saveFigure(target, id, field, index, { spec, source, expectedUpdatedAt }, getAccessToken) {
  const payload = { spec: spec ?? null };
  if (spec != null && source) payload.source = source;
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}`,
    { method: 'PATCH', payload },
    getAccessToken,
    '그림 저장에 실패했습니다.',
  );
}

/**
 * **OCR 검수 전용**이라 여기서 막는다 — 계약 §10 이 「문제은행에는 만들지 않았습니다」로 못박았고
 * (등록은 은행에서 편집기 저장이 곧 그림 목록 반영이라 필요가 없다), 교체(`PATCH {image}`)의
 * 은행 쪽은 **다음 배치**다(회신 §3-3). 은행 경로로 부르면 404 가 오는데, 그러면 화면은
 * 「그림 등록에 실패했습니다 (404)」라고만 말하고 사람은 원인을 찾지 못한다.
 *
 * 이 저장소는 「모르는 것으로 막지 않는다」를 원칙으로 쓰지만(figureCapabilities 주석),
 * 이 둘은 모르는 것이 아니라 **없다고 확인된 것**이다. 1차 방어는 호출부가 버튼을 내지 않는 것이고
 * 여기는 2차다 — 사람 눈에 닿기 전에 개발 중에 걸리라고 둔다.
 */
function assertOcrOnly(target, what) {
  if (target !== 'ocr') throw new Error(`${what}는 OCR 검수에서만 됩니다(계약 §10).`);
}

/**
 * 올린 이미지를 **새 그림으로 등록** — POST {base}/figures (계약 §10 · 회신 §1, 2026-09-04)
 *
 * 검수자가 편집기에 붙여넣은 이미지를 서버가 세는 그림으로 만든다. 이것이 없으면 그 이미지는
 * 편집본에만 남아 그림 목록과 개수가 어긋나고, 그 field 의 그림 기능이 통째로 잠긴다.
 *
 * · `at` — 그 자리 그림 **바로 앞**에 삽입한다. 새 그림의 index 가 곧 `at` 이고 뒤 그림은 +1
 *   (삭제 API 의 역방향). 생략하거나 범위 밖이면 맨 끝이다. **정수가 아니면 싣지 않는다** —
 *   서버가 400 `at` 을 내는 값을 화면이 만들지 않는다.
 * · `field` — 안 보내면 서버 기본값 body 다. 명시해 보낸다: 풀이에 넣으려던 것이 본문에 붙으면
 *   되돌리는 길이 삭제뿐이다(generateFigure 가 같은 이유로 같은 수법을 쓴다).
 * · 이력 **900 등록**, `has_original:true`(원본 = 그 이미지) → 되돌리기·재요청·spec:null 복귀 가능.
 * · LLM 을 쓰지 않는다. 즉시 돌아온다(재요청의 35~100초와 다르다).
 *
 * 응답 `{ created, item, index, updated_at, edited_copy_dropped }` — `item.src` 는 보낸 image 와
 * **바이트 동일**이라 그대로 편집기 노드에 쓸 수 있다(회신 §9-2 가 권한 순서).
 */
export async function registerFigure(target, id, { field, at, image, expectedUpdatedAt } = {}, getAccessToken) {
  assertOcrOnly(target, '그림 등록');
  const payload = { image };
  if (field) payload.field = field;
  if (Number.isInteger(at) && at >= 0) payload.at = at;
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return call(
    `${figureBase(target, id)}/figures`,
    { method: 'POST', payload },
    getAccessToken,
    '그림 등록에 실패했습니다.',
  );
}

/**
 * 그 자리 그림을 **올린 이미지로 교체** — PATCH {base}/figures/{field}/{index} `{image}`
 * (계약 §10 · 회신 §3, 2026-09-04)
 *
 * **saveFigure 와 함수를 가른 이유**: 그쪽은 `payload = { spec: spec ?? null }` 로 **항상 spec 키를
 * 싣는다**(명세 제거가 `spec:null` 이라 키를 빼면 뜻이 사라진다). 그런데 계약은 `spec` 과 `image`
 * 를 함께 보내면 **400** 이다. 한 함수에 조건 분기로 넣으면 다음 사람이 spec 쪽 기본값을
 * 손볼 때 조용히 400 을 만든다. patchTraceTexts 를 따로 둔 것과 같은 판단이다.
 *
 * 결과: 그 블록이 자체 이미지 픽셀 블록이 된다 — 명세를 버리고 `kind:"pixel"`, 이력 **950 교체**,
 * 원본 보관(`has_original:true`). 원래 bbox 는 남아 `crop`(페이지 원본 크롭)은 계속 온다.
 */
export async function replaceFigureImage(target, id, field, index, { image, expectedUpdatedAt } = {}, getAccessToken) {
  // 2026-09-05 부터 **은행도 받는다**(계약 §10-2 G1 · BE-20260905-reply-figure-parity.md §2) — 같은 검증 모듈,
  // 이력 950, 원본 = 그 이미지. 종전의 OCR 전용 가드(assertOcrOnly)는 등록(POST)에만 남는다.
  const payload = { image };
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}`,
    { method: 'PATCH', payload },
    getAccessToken,
    '이미지 교체에 실패했습니다.',
  );
}

/**
 * 그림 하나만 AI 에 다시 요청 — POST {base}/figures/{field}/{index}/reconvert (계약 §3-3·§4-3)
 *
 * ⚠ **35~100초 동기 호출**이다. 그 사이 다른 편집이 들어올 수 있어 expected_updated_at 이
 * 사실상 필수다. 같은 그림·같은 문제에 동시 요청은 409 in_progress.
 *
 * ⚠ 응답 200 에 두 갈래가 있다 — { converted:true, item, updated_at } 과
 * **{ converted:false, issues[] }(무변경)**. 뒤엣것은 실패가 아니다: 좌표·식으로 옮길 수 없는
 * 그림이라 원본을 그대로 둔 것이고 손실이 없다. 이 저장소는 같은 자리에서 한 번 헛디뎠고
 * 그 교훈이 problemApi.js:349 에 적혀 있다.
 *
 * ⚠ 그 거절 응답에 **`suggest:"trace"`** 가 함께 올 수 있다(계약 §9-4) — 「사진이라 좌표·식으로는
 * 못 옮기지만 **윤곽을 따라 그리는 길**은 남아 있다」는 뜻이다. 화면은 그때 [사진 따라 그리기]를
 * 낸다. 이것을 안 보이면 사용자에게는 그냥 막다른 길이 된다.
 *
 * mode: 계약 §9-4 의 변환 모드. FIGURE_MODES 주석 참조.
 *
 * 무엇을 재료로 다시 그릴지는 buildReconvertPayload 가 정한다(그 주석의 표를 보라).
 */
export async function reconvertFigure(target, id, field, index, opts = {}, getAccessToken) {
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}/reconvert`,
    { method: 'POST', payload: buildReconvertPayload(opts) },
    getAccessToken,
    'AI 재요청에 실패했습니다.',
  );
}

/**
 * 재요청 body 를 만든다 — **순수 함수로 떼어 두고 시험으로 덮는다**(figureApi.test.js).
 *
 * 여기 한 줄이 틀리면 400 이 오거나, 더 나쁘게는 **엉뚱한 재료로 조용히 그려진다** —
 * 서버는 무엇을 보고 그렸는지 응답에 적지 않으므로 화면에서 알아볼 방법이 없다.
 *
 * ── 서버가 무엇을 보고 다시 그리는가 (계약 §4-3 · §9-4 의 2026-09-01 추가) ──
 * | 보내는 것 | 재료 |
 * |---|---|
 * | `{}` | ① `body.image` → ② 지금 그림이 픽셀이면 그 픽셀 → ③ SVG 면 이력 보관 원본 |
 * | `{image}` | 그 이미지(위 ①) |
 * | `{use_original:true}` | **이력 보관 원본으로 강제** — 지금 그림도 `body.image` 도 무시. 없으면 409 `no_original` |
 *
 * `use_original` 은 화면 그림을 구워 보내던 길(2026-08-30)보다 **원본에 가깝다.** 그쪽은
 * `original_src` 를 캔버스로 한 번 다시 구워(PNG 재인코딩) 보내는 것이라 서버가 보관한
 * 바로 그 픽셀이 아니고, 원격 주소·캔버스 오염이면 아예 굽지 못해 그 선택지가 사라졌다.
 *
 * ⚠ 둘을 **함께 싣지 않는다.** 서버가 `image` 를 무시하므로 결과는 같지만, 수백 KB 짜리
 * data-URI 를 「쓰이지 않을 것」으로 올려 보내는 요청이 되고, 나중에 이 요청을 되짚을 때
 * body 가 무엇을 재료로 썼는지 거짓말을 한다.
 *
 * image: data-URI 다. 원격 주소(S3 등)를 프런트가 읽는 길은 CORS 로 조용히 실패하므로
 * 여기서 만들지 않는다 — 호출부가 캔버스로 굽거나 파일을 읽어 data-URI 로 넘긴다.
 * OCR 은 서버가 페이지에서 bbox 로 잘라 쓰므로(§3-3) 두 인자 모두 필요 없다.
 */
export function buildReconvertPayload({
  expectedUpdatedAt, userPrompt, image, mode, useOriginal,
} = {}) {
  const payload = {};
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  // 변환 모드(계약 §9-4) — 기본(faithful)이면 키를 싣지 않는다(FIGURE_MODES 주석).
  const modeValue = figureModePayload(mode);
  if (modeValue) payload.mode = modeValue;
  if (useOriginal) payload.use_original = true;
  else if (typeof image === 'string' && image.trim()) payload.image = image;
  // 사용자 지시(선택) — 「점선은 무시하라」처럼 이 그림에만 통하는 말. 비면 키를 싣지 않는다.
  // 서버가 받는다(회신 §4-1 — 그림 재요청 두 경로 모두 ✅). 공통 규칙은 회신 §4-2:
  // strip 후 **500자 이하**(초과 400)·프롬프트 **말미**에 `[추가 지시]` 블록으로 붙고
  // **옵션·규칙과 충돌하면 그쪽이 이긴다**. 이 버전은 이력 `versions[].user_prompt` 에 남는다(계약 §8).
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  return payload;
}

/**
 * 트레이스 그림 속 **글자만** 바꾼다 — PATCH {base}/figures/{field}/{index} (계약 §9-4, 2026-08-31)
 *
 * 「사진 따라 그리기」로 만든 그림은 윤곽이 벡터 경로이고, 그림 속 인쇄 문구는 그 위에 얹힌
 * **진짜 `<text>`**(id t1·t2…)다. 그래서 사진을 다시 찍지 않고 글자만 갈아 끼울 수 있다 —
 * 계약의 실사례가 「61654 대관람차: 트레이스 뒤 5명 → 6명」이고, 이것이 트레이스를 픽셀 대신
 * 쓰는 **이유**다(문제를 변형하면 그림 속 수치도 따라가야 한다).
 *
 * traceTexts: `{ "t2": "• 최대 탑승인원 : 6명" }` — **바뀐 것만** 보낸다. 계약의 예시가 그 모양이고,
 * 안 바뀐 글자까지 실으면 서버가 그것도 「사람이 고친 값」으로 버전에 남긴다.
 *
 * ⚠ **`spec` 을 싣지 않는다.** 계약 §1-4 는 「`spec` 키 없음 → 400」이라 했는데 §9-4 는 body 를
 * `{trace_texts, expected_updated_at?}` 로만 적었다. 두 문장이 어긋나면 **나중 것·구체적인 것**을
 * 따른다. 그래서 saveFigure 와 **함수를 갈라 둔다** — 400 이 오면 여기 한 줄에 spec 을 더하면 되고,
 * 합쳐 두었으면 그림 저장 경로 전체를 건드려야 한다.
 *
 * **2026-09-01 dev 실호출로 확정됐다**(문제 61654 body/0):
 *   요청 `{trace_texts:{t2:"• 최대 탑승인원 : 6명"}, expected_updated_at:"2026-09-01T03:38:31.418768"}`
 *   → **200** `message:"trace texts edited"` · data `{applied:["t2"], item, updated_at:"…03:53:32.840641"}`
 * 즉 ① spec 없이 200 이다(§1-4 는 이 경로에 해당하지 않는다) ② **`updated_at` 이 온다**
 * (계약에 없던 것 — 그래도 호출부의 「안 오면 다시 읽는다」 방어는 남긴다. 계약의 말이 아니라
 * 우리 실측이라, 다음 배포에서 조용히 사라져도 화면이 잠기지 않아야 한다)
 * ③ **응답 item 에 `trace_texts` 가 없다** — readTraceTexts 가 그림에서 읽는 이유가 이것이다.
 *
 * 트레이스로 만든 그림이 아니면 **409 `not_traced`**(figureText 가 recoverable 로 다룬다 —
 * 글자 하나 못 바꾼 것으로 그림 편집 전체를 잠그지 않는다).
 */
export async function patchTraceTexts(target, id, field, index, { traceTexts, expectedUpdatedAt } = {}, getAccessToken) {
  const payload = { trace_texts: traceTexts || {} };
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}`,
    { method: 'PATCH', payload },
    getAccessToken,
    '그림 속 글자를 바꾸지 못했습니다.',
  );
}

/**
 * 항목에서 그림 속 글자 목록을 꺼낸다 — `[{ id, content, … }]`.
 *
 * 계약 §9-4 가 「응답 `trace_texts:[{id,content,…}]`」라고만 적어 **어느 자리에 실리는지**를
 * 말하지 않았다(응답 최상위인지 `item` 안인지). 둘 다 본다 — 어느 쪽이 답이어도 화면은 옳다.
 *
 * ── 2026-09-01 실측: **셋 다 안 온다** ──
 * 문제 61654 body/0 으로 `GET …/figures` · `GET …/history` · `PATCH {trace_texts}` 를 다 찔러 봤고
 * 세 응답의 item 키가 전부 14개(field·index·kind·spec·spec_source·svg·src·width·height·alt·
 * issues·history_version·has_original·original_src)로 같았다 — `trace_texts` 가 없다.
 * 그래서 이 함수가 늘 빈 배열을 돌려주었고, 그것을 열쇠로 삼은 「그림 속 글자」 칸은
 * **한 번도 뜬 적이 없다.** 유일한 정본 경로(patchTraceTexts)가 화면에서 닿을 수 없었다는 뜻이다.
 *
 * 그래서 **그림 자신에게서 읽는 길을 뒤에 둔다**(figureTrace). 서버가 언제 그 키를 실어 줄지는
 * 우리가 정할 수 없고, 값은 이미 `item.src` 안에 들어와 있다(`<metadata id="figtrace">`).
 * 순서는 **응답이 먼저**다 — 서버가 실어 주기 시작하면 그쪽이 정본이 되고 이 폴백은 조용히 쉰다.
 *
 * 그림을 고르는 순서(`svg || src`)는 **화면과 같아야 한다**(FigureModal 의 sendNow·미리보기 칸).
 * 다르면 화면이 그리는 그림과 글자를 읽는 그림이 갈린다.
 *
 * `id` 와 `content` 만 쓴다. 「…」에 무엇이 더 오는지는 모르고, 자리(좌표)가 온다면
 * 그림 위에 겹쳐 고치게 할 수 있다 — 그것은 화면 정리 몫이다.
 */
export function readTraceTexts(source) {
  /**
   * **서버가 배열을 보냈으면 그것이 정본이다 — 비어 있어도 그렇다.**
   *
   * `length > 0` 으로 가르면 서버가 「이 그림은 트레이스가 아니다」를 `[]` 로 말했을 때 그 말을
   * 무시하고 그림을 뒤지게 된다. 왕복 문서(FE-20260901-…md §3-1)에서 백엔드에 「키를 안 보내거나
   * `[]` 로 주세요, 저희는 배열이면 그것이 정본으로 씁니다」라고 약속한 것도 이 모양이다.
   * 약속한 규칙과 코드가 갈리면, 그 약속을 지킨 쪽이 틀리게 된다.
   */
  const fromTop = Array.isArray(source?.trace_texts) ? source.trace_texts : null;
  const fromItem = Array.isArray(source?.item?.trace_texts) ? source.item.trace_texts : null;
  const list = fromTop ?? fromItem;
  if (list) {
    return list
      .filter((t) => t && typeof t.id === 'string' && t.id !== '')
      .map((t) => ({ id: t.id, content: typeof t.content === 'string' ? t.content : '' }));
  }
  // 봉투로도 알맹이로도 불린다(FigureModal: settle 은 `d`, 나머지 셋은 `item`) — 두 모양을 다 본다.
  const fromSelfSvg = readTraceTextsFromSvg(source?.svg || source?.src);
  if (fromSelfSvg.length > 0) return fromSelfSvg;
  return readTraceTextsFromSvg(source?.item?.svg || source?.item?.src);
}

/**
 * 서버가 **바로잡은 곳** 목록 — `corrections[]` (계약 §9-4, 2026-08-31).
 *
 * 「변환 시 모델이 잰 픽셀 관측(observed)으로 서버가 좌표를 결정적으로 교정(대칭화·직각 방향
 * 오류 해소). 결과 `corrections[]` 동봉」. AI 가 눈대중으로 잰 값을 서버가 규칙으로 다듬었다는
 * 뜻이라, **원본과 나란히 대조하는 이 화면에서 값이 크다** — 어디가 사람 손을 탔는지 알려 준다.
 *
 * ⚠ **모양이 계약에 없다.** 실리는 자리(응답 최상위인지 `item` 안인지)도 없다. 그래서
 * ① 두 자리를 다 보고 ② **문자열만 남긴다.** 객체가 오면 아무것도 안 그린다 —
 *  `[object Object]` 를 화면에 내는 것보다 안 내는 편이 낫고, 그때는 확인해서 문구를 지으면 된다.
 * (→ dev 실호출로 확인할 것 §3)
 */
export function readCorrections(source) {
  const list = Array.isArray(source?.corrections)
    ? source.corrections
    : (Array.isArray(source?.item?.corrections) ? source.item.corrections : []);
  return list.filter((c) => typeof c === 'string' && c.trim() !== '');
}

/**
 * 그림이 **없는** 문제에 그림을 만든다 — POST {base}/figures/generate (계약 §9-2, 2026-08-30)
 *
 * 본문 텍스트(+사람의 지시)를 LLM 이 읽고 명세를 지어 검증·렌더한 뒤 **그 field 맨 끝에**
 * image 노드를 붙인다. 기존 그림을 바꾸는 것이 아니라 **더하는** 것이다 — 대체하려면
 * 있던 그림을 deleteFigure 로 지우고 부른다.
 *
 * ⚠ **수십 초 동기 호출**이다(reconvert 와 같은 갈래). 그 사이 다른 편집이 들어올 수 있어
 * expected_updated_at 을 보낸다.
 *
 * ⚠ 응답 200 에 두 갈래가 있다 — { generated:true, item, updated_at } 과
 * **{ generated:false, issues[] }(무변경)**. 뒤엣것은 실패가 아니다: 모델이 「그림이 필요 없다」
 * 또는 「확정할 수 없다」고 판단해 본문을 그대로 둔 것이라 손실이 없다. reconvert 의
 * `converted:false` 와 똑같은 함정이고, 이 저장소는 그 자리에서 이미 한 번 헛디뎠다
 * (problemApi.js:349). 오류로 다루면 사용자가 「망가졌다」고 읽는다.
 *
 * ⚠ **본문을 바꾸는 쓰기다.** 문제은행은 본문 이력에 버전이 하나 쌓이고 Problems.updated_at 이
 * 전진한다(계약 §9-2 「본문이력·status 999·낙관락은 PATCH 와 동일」). 그래서 호출부는
 * **미저장 편집분이 있으면 부르지 않아야 한다** — 부르면 사람이 고치던 것 위에 서버 본문이 덮인다.
 *
 * field 를 안 보내면 서버 기본값(body)이다. 명시해 보낸다 — 풀이에 만들려던 것이 본문에 붙으면
 * 되돌리는 길이 삭제뿐이다.
 *
 * mode: 「어떻게 그릴까」 — faithful(도판형) · schematic(도식형). **trace 는 400**(따라 그릴 사진이 없다).
 *   계약 §10-2 G6 · BE-20260905-reply-figure-parity.md §5(응답에도 `mode` 가 실린다). 재요청과 같은 규칙으로
 *   **기본값이면 키를 싣지 않는다** — 옛 배포본에 400 을 맞을 자리를 만들지 않는다(figureModePayload).
 */
export async function generateFigure(target, id, {
  field, userPrompt, expectedUpdatedAt, mode,
} = {}, getAccessToken) {
  const payload = {};
  if (field) payload.field = field;
  const modeValue = figureModePayload(mode);
  if (modeValue) payload.mode = modeValue;
  // 지시(선택) — 계약 §9-2 의 user_prompt(≤500자). 회신 §4-2 의 공통 규칙을 따른다:
  // 프롬프트 **말미**에 `[추가 지시]` 로 붙고, 이력 버전(800 생성)에 그대로 기록된다.
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return call(
    `${figureBase(target, id)}/figures/generate`,
    { method: 'POST', payload },
    getAccessToken,
    '그림 생성에 실패했습니다.',
  );
}

/**
 * 그림 하나의 이력 — GET {base}/figures/{field}/{index}/history (계약 §3-4·§4-4)
 * data = { current_version, versions[] } — 최신순. 문제은행은 item(현재 그림)도 함께 온다.
 * 이력은 append-only 다 — 되돌려도 과거 버전은 남고 새 버전이 쌓인다.
 * 목록과 마찬가지로 **첫 조회가 version 1 을 기록**한다(계약 §6).
 */
export async function fetchFigureHistory(target, id, field, index, getAccessToken) {
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}/history`,
    {},
    getAccessToken,
    '그림 이력을 불러오지 못했습니다.',
  );
}

/**
 * 판 하나의 상세 — GET {base}/figures/{field}/{index}/history/{version}
 * (계약 「판 내용 스냅샷」 절 · 09-01, dev 427a52c)
 *
 * data 에 **src** 로 그 판의 실제 내용이 동봉된다(우선순위: 스냅샷 > spec 렌더 > 보관 원본).
 * spec 없는 판(트레이스·픽셀)도 이것으로 「같은 사진」 한 장이 아니라 제 그림을 보인다.
 *
 * ⚠ **src 가 크다 — lazy 로만 부른다**(계약 「lazy 권장 — src 가 큼」). 목록을 열 때 전 판을
 * 받지 않고, [보기]가 가리킨 그 판만 1건 받는다. 버전은 append-only 로 불변이라(계약 §1-5)
 * 받은 것을 버전 키로 캐시하면 낡을 일이 없다.
 *
 * **순수 조회다 — 관측(seed) 판을 남기지 않는다**(백엔드 확정 09-01, 계약 스냅샷 절에 명시).
 * seed 를 만드는 것은 목록 둘(GET …/figures · GET …/history)뿐이라, 판 상세를 아무리 눌러도
 * 이력이 늘지 않는다 — restore 둘러보기로 판이 쌓이던 그 함정이 여기엔 없다.
 * 엣지 하나: 이력이 아예 비어 있으면 seed 없이 404 다. 판 번호는 어차피 목록에서 얻으므로
 * 화면은 언제나 목록 → 상세 순서다(이 함수를 목록 없이 부르지 마라). 없는 version 도 404.
 */
export async function fetchFigureHistoryVersion(target, id, field, index, version, getAccessToken) {
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}/history/${version}`,
    {},
    getAccessToken,
    '그 버전의 그림을 불러오지 못했습니다.',
  );
}

/**
 * 판 상세 응답에서 **그릴 한 벌**을 꺼낸다 — `{ src, width, height, alt } | null`.
 *
 * 계약이 「src 로 동봉」이라고만 적고 실리는 자리(응답 최상위인지 item 안인지)를 말하지 않았다 —
 * readTraceTexts·readCorrections 와 같은 상황이라 같은 규칙을 쓴다: **두 자리를 다 본다.**
 * src 가 어느 자리에도 없으면 null — 호출부는 그 판만 종전 폴백(spec 렌더/보관 원본)으로
 * 물러난다(판 하나가 안 온 것으로 목록 전체를 죽이지 않는다).
 * 크기는 숫자일 때만 싣는다 — 문자열 "300" 이 오면 화면의 배율 계산이 조용히 어긋난다.
 */
export function readVersionContent(source) {
  const pick = (o) => {
    if (!o || typeof o.src !== 'string' || o.src.trim() === '') return null;
    return {
      src: o.src,
      ...(typeof o.svg_text === 'string' ? { svg_text: o.svg_text } : {}),
      ...(Number.isInteger(o.revision) ? { revision: o.revision } : {}),
      width: Number.isFinite(o.width) ? o.width : null,
      height: Number.isFinite(o.height) ? o.height : null,
      alt: typeof o.alt === 'string' ? o.alt : '',
    };
  };
  return pick(source) || pick(source?.item);
}

/**
 * 그 버전으로 되돌리기 — POST {base}/figures/{field}/{index}/history/{version}/restore (계약 §3-4)
 *
 * ⚠ **[저장] 시점에 1회만 부른다**(2026-09-01 · FigureModal.doRestore). 2026-08-30 에 이 경로를
 * 통째로 걷어낸 이유는 「지난 버전을 골라 본다」가 그대로 이력이 됐기 때문이다(이력은
 * append-only 라 · 계약 §1-5 · v4~v13 이 전부 사유 500) — 오너 재확인 「되돌리기 할 때마다
 * 버전 만드는 건 잘못된 기능」. 그 원칙(고르기는 서버에 안 나간다)은 그대로다: 고르기는 화면
 * 안의 일이고, 이 호출은 사람이 [저장]을 눌렀을 때 한 번 나간다. 버전은 여전히 저장할 때만 는다.
 *
 * 다시 살린 까닭은 계약 「판 내용 스냅샷」(09-01)이다 — spec 없는 판(트레이스·픽셀,
 * has_content=true)의 내용을 되살리는 쓰기는 **restore 뿐이다**(그 내용을 싣는 PATCH 가 계약에
 * 없다). `PATCH {spec:null}` 로 보내면 판 내용(글자 수정 포함)을 버리고 보관 원본으로 가 버린다.
 * 좌표·식 버전과 사진(원본) 회귀는 종전대로 [저장]의 `PATCH {spec}`·`PATCH {spec:null}` 이다 —
 * 그쪽까지 restore 로 돌리면 caps.canDropSpec 잠금·no_original 409 처리와 어긋난다.
 *
 * expectedUpdatedAt 은 항상 싣는다 — deleteFigure 와 같은 근거(§1-3 은 모든 쓰기에 필수라 했다).
 * data = { item, restored_from, updated_at }. 없는 version 은 404. 이력 사유는 500(되돌리기)로 남는다.
 */
export async function restoreFigure(target, id, field, index, version, { expectedUpdatedAt } = {}, getAccessToken) {
  const payload = {};
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}/history/${version}/restore`,
    { method: 'POST', payload },
    getAccessToken,
    '되돌리기에 실패했습니다.',
  );
}

/**
 * 그림 하나를 본문에서 지운다 — DELETE {base}/figures/{field}/{index} (계약 §8 · 회신 §2 Q8)
 * data = { deleted:true, field, index, remaining, updated_at }. **item 이 없다.**
 *
 * ⚠ **이 길 말고는 없다.** 종전에는 편집기 안에서 image 노드를 지우고 본문 PATCH 로 내보냈는데,
 * 회신 §2 Q8 이 그것을 정면으로 금지했다 — 「편집기 PATCH 로 image 노드를 지우면 서버가 알 수 없어
 * 이력이 어긋납니다. 그림 삭제는 반드시 이 API 로.」 서버는 이 호출에서 노드/블록을 본문에서 빼고,
 * 그 그림의 이력에 **삭제 버전(source 700)** 을 남긴 뒤 퇴장시키며(조회 불가),
 * **뒤 그림(index+1…)의 이력을 한 칸 앞으로 당겨** 본문의 새 index 와 계속 맞춘다.
 *
 * expected_updated_at 은 계약 §8 표에서 선택(`?`)이지만 **항상 보낸다** — §1-3 은 모든 쓰기에
 * 필수라 했고 두 문장이 어긋난다. 생략했을 때의 동작을 우리가 모르는 이상, 보내서 손해 볼 일이 없다.
 *
 * payload 를 빈 객체로라도 넘겨야 call() 이 Content-Type 과 body 를 싣는다 —
 * undefined 를 넘기면 body 없는 DELETE 로 나가 낙관락이 통째로 빠진다.
 *
 * 409(stale)·404(index 범위 밖)·423(OCR 승격·프로젝트 종료)은 **여기서 가르지 않는다** —
 * 나머지 넷과 같이 던져서 describeFigureError 가 한자리에서 갈라 문구를 낸다.
 */
export async function deleteFigure(target, id, field, index, { expectedUpdatedAt } = {}, getAccessToken) {
  const payload = {};
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return call(
    `${figureBase(target, id)}/figures/${field}/${index}`,
    { method: 'DELETE', payload },
    getAccessToken,
    '그림 삭제에 실패했습니다.',
  );
}

/**
 * **저장하기 전에, 본문에서 사라진 그림을 서버에서도 지운다.**
 *
 * 종전에는 그림이 줄어든 저장을 막았다(회신 §2 Q8 — 본문 PATCH 로 노드를 없애면 서버가 몰라
 * 이력이 어긋난다). 그런데 그 안내가 시키는 [삭제] 버튼도 순번 대조에 막혀 있어 두 관문이
 * 서로를 가리켰다. 막는 대신 **어떤 방법으로 지우든 삭제 API 를 타게** 하면 이력이 맞는다.
 *
 * 순서가 중요하다 — 삭제를 **먼저** 하고 본문 저장을 나중에 한다. 반대로 하면 본문 PATCH 가
 * 노드를 없앤 채 나가 서버가 모르는 삭제가 되고, 그것이 애초에 막으려던 일이다.
 * 그래서 삭제가 실패하면 본문 저장도 하지 않는다(호출부가 ok 를 보고 멈춘다).
 *
 * ⚠ **문서가 최신이라는 전제 위에 선다.** 그림 창에서 방금 고쳐 서버가 앞서 있으면 서버의 새 src
 * 가 문서에 없어 「지워졌다」로 오판한다. 실제로는 그 창이 닫힐 때 호스트가 상세를 다시 읽어
 * (ProblemViewTab·OcrReviewModal 의 closeFigure) 그 상태가 남지 않는다 — 그 기존 동작이 이
 * 함수의 전제를 지킨다. 닫기 없이 문서를 낡게 만드는 경로가 새로 생기면 여기가 함께 흔들린다.
 *
 * @param plans [{ field, nodes }] — nodes 는 **저장할 새 문서**의 collectFigureNodes 결과
 * @returns { ok:true, updatedAt, deleted } | { ok:false, reason, field } | AUTH_REDIRECT
 */
export async function purgeRemovedFigures(target, id, plans, { expectedUpdatedAt } = {}, getAccessToken) {
  const d = await fetchFigures(target, id, getAccessToken);
  if (d === AUTH_REDIRECT) return AUTH_REDIRECT;
  const items = Array.isArray(d?.items) ? d.items : [];
  // 목록 응답의 토큰이 가장 새것이다(첫 조회의 seed 는 이 값을 전진시키지 않는다 — 회신 §2 Q4 실측).
  let token = d?.updated_at || expectedUpdatedAt;
  const deleted = {};
  for (const { field, nodes } of plans) {
    const plan = planFigureDeletions(items, nodes, field);
    if (!plan.ok) return { ok: false, reason: plan.reason, field };
    if (plan.targets.length === 0) continue;
    // field 가 다르면 순번이 서로 독립이라(본문 0..n · 풀이 0..m) 한 쪽 삭제가 다른 쪽을 밀지 않는다.
    // eslint-disable-next-line no-await-in-loop
    const r = await deleteFiguresInOrder(target, id, field, plan.targets, { expectedUpdatedAt: token }, getAccessToken);
    if (r === AUTH_REDIRECT) return AUTH_REDIRECT;
    token = r.updatedAt;
    deleted[field] = r.deleted;
  }
  return { ok: true, updatedAt: token, deleted };
}

/**
 * **저장하기 전에, 본문에 새로 들어온 이미지를 서버 그림으로 등록한다** — `purgeRemovedFigures`
 * 의 정확한 역방향이다(오너 지시 2026-09-04 「등록 안 된 그림을 등록하도록」).
 *
 * 왜 저장 시점인가: 검수자가 이미지를 붙여넣는 방법은 여럿이고(툴바·드롭·클립보드·웹페이지 복사)
 * 그중 웹페이지 복사는 삽입 명령을 타지 않아 붙여넣기 창이 뜨지 않는다. 어느 길로 들어왔든
 * **저장할 때 한 번 훑으면** 서버가 모르는 그림이 남지 않는다.
 *
 * ── `at` 은 한 번의 순회로 나온다 ──
 * 문서를 앞에서부터 훑으며 **서버가 아는 그림의 수**를 센다. 그 값이 곧 지금 노드의 서버 순번이다.
 * 등록한 것도 그 뒤로는 「아는 그림」이 되므로 카운터에 함께 넣는다 — 그래서 여러 장을 넣어도
 * 순번이 밀리지 않는다.
 *
 * ── 등록할 수 없는 것의 처분 ──
 *  · `src` 없는 노드(만료 픽셀) — 서버가 이미 세는 그림이다. 등록하지 않고 **순번만 차지**한다.
 *    종전에는 이것을 `no_src` 로 막아 **그 field 의 글 한 줄도 저장할 수 없었다**(2026-09-04 정정).
 *  · 웹 주소(`http…`) 이미지 — 서버가 받지 않는 값이다(data-URI 만). 저장을 막지 않고 **건너뛴다**
 *    — 참고 이미지로 남고, 호출부가 그 수(`skipped`)를 사람에게 말한다.
 *  · data-URI 인데 규격 밖(형식·2MB) — 붙여넣는 순간 화면이 먼저 막지만(호스트), 다른 길로 들어온
 *    것은 여기서 멈춘다. 서버가 400·413 으로 거절할 값을 올려 보내지 않는다.
 *
 * ⚠ 여러 장을 등록하는 중간 단계에서는 편집본 노드 수와 서버 그림 수가 어긋나, 서버가 그 field 의
 * 편집본을 폐기할 수 있다(회신 §2 의 대조 조건). **그래도 손실이 없다** — 이 함수 뒤에 우리가
 * 본문 PATCH 로 편집본을 통째로 다시 쓰기 때문이다. 그래서 여기서는 `edited_copy_dropped` 를
 * 읽지 않는다.
 *
 * @param plans [{ field, nodes }] — nodes 는 **저장할 새 문서**의 collectFigureNodes 결과
 * @returns { ok:true, updatedAt, registered, skipped } | { ok:false, reason, field } | AUTH_REDIRECT
 */
export async function registerNewFigures(target, id, plans, { expectedUpdatedAt } = {}, getAccessToken) {
  const d = await fetchFigures(target, id, getAccessToken);
  if (d === AUTH_REDIRECT) return AUTH_REDIRECT;
  const items = Array.isArray(d?.items) ? d.items : [];
  let token = d?.updated_at || expectedUpdatedAt;
  const registered = {};
  const skipped = {};
  const hasSrc = (v) => typeof v === 'string' && v !== '';
  for (const { field, nodes } of plans) {
    const srcs = items.filter((it) => it && it.field === field).map((it) => it.src).filter(hasSrc);

    /**
     * **서버 사본을 개수로 물린다**(2026-09-04). 문서를 앞에서부터 읽으며 서버 사본을 하나씩
     * 소진하면, 같은 그림이 서버나 문서에 둘 있어도 순번(`at`)이 어긋나지 않는다.
     *
     * 예: 서버 [X, X] · 문서 [X, 새것, X] → 첫 X 가 사본 하나를 물어 `at`=1, 새것이 1번 자리에
     * 들어가 뒤 X 를 2번으로 민다. 결과는 문서 순서와 같다.
     *
     * 소진하고 남은 문서 이미지는 **사본마다 따로 등록한다.** 같은 그림을 두 번 붙여넣은 것은
     * 그림이 두 장이라는 뜻이고, 한 번만 등록하면 나머지 한 장이 영원히 등록되지 않아
     * 그 field 의 그림 편집이 계속 잠긴다.
     */
    const remaining = srcs.reduce((m, v) => m.set(v, (m.get(v) || 0) + 1), new Map());
    const done = [];
    let at = 0;
    for (const n of nodes) {
      const src = n?.src;
      // 서버가 세는 자리(만료 그림)다 — 순번만 올린다.
      if (!hasSrc(src)) { at += 1; continue; }
      const left = remaining.get(src) || 0;
      if (left > 0) { remaining.set(src, left - 1); at += 1; continue; }
      // 웹 주소 이미지 — 등록할 수 없는 값이다. 순번을 올리지 않는다(서버가 세지 않는 그림이다).
      if (!/^data:/i.test(src)) { skipped[field] = (skipped[field] || 0) + 1; continue; }
      const check = parseImageDataUri(src);
      if (!check.ok) return { ok: false, reason: check.reason === 'size' ? 'too_large' : 'bad_image', field };
      // eslint-disable-next-line no-await-in-loop -- 앞선 등록이 뒤 순번을 밀므로 반드시 차례로 보낸다
      const r = await registerFigure(target, id, { field, at, image: src, expectedUpdatedAt: token }, getAccessToken);
      if (r === AUTH_REDIRECT) return AUTH_REDIRECT;
      if (r?.updated_at) token = r.updated_at;
      done.push(at);
      at += 1;
    }
    if (done.length > 0) registered[field] = done;
  }
  return { ok: true, updatedAt: token, registered, skipped };
}

/**
 * 사라진 그림들을 **순번이 큰 것부터 차례로** 지운다 — 저장할 때 함께 지우기 위한 실행부다.
 *
 * · **큰 순번부터**인 이유: 삭제는 뒤 그림의 순번을 한 칸 당긴다(회신 §2 Q8). 작은 것부터 지우면
 *   두 번째 요청이 이미 당겨진 순번을 가리켜 **엉뚱한 그림을 지운다.**
 * · **낙관락을 이어 쓴다**: 삭제마다 `updated_at` 이 전진하므로 앞 응답의 값을 다음 요청에 싣는다.
 *   안 그러면 두 번째부터 409 stale 이다.
 * · **중간에 실패하면 멈춘다.** 이미 지운 것은 서버에 반영돼 있으므로 그 사실을 에러에 실어
 *   호출부가 사람에게 말할 수 있게 한다 — 부분 반영을 조용히 두는 것이 가장 나쁜 결말이다.
 *   본문 저장은 호출부가 이 함수의 성공을 보고 나서 한다.
 *
 * `remaining` 은 읽지 않는다 — 그 field 의 남은 수인지 문제 전체인지 계약에 없다.
 *
 * @returns { deleted:number[], updatedAt } — 지운 순번들과 이어 쓸 낙관락 토큰
 */
export async function deleteFiguresInOrder(target, id, field, indices, { expectedUpdatedAt } = {}, getAccessToken) {
  let token = expectedUpdatedAt;
  const deleted = [];
  for (const index of indices) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const d = await deleteFigure(target, id, field, index, { expectedUpdatedAt: token }, getAccessToken);
      if (d === AUTH_REDIRECT) return AUTH_REDIRECT;
      if (d?.updated_at) token = d.updated_at;
      deleted.push(index);
    } catch (e) {
      e.partial = { deleted, updatedAt: token, field };
      throw e;
    }
  }
  return { deleted, updatedAt: token };
}
